import React, { useEffect, useState } from 'react';
import { api, fmtDate, getToken } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, EmptyState, Spinner, SearchInput, useToast, Modal } from '../components/UI';
const PERMISSIONS = [
  { key:'view_declarations',    label:'View Declarations' },
  { key:'approve_declarations', label:'Approve Declarations' },
  { key:'edit_declarations',    label:'Edit Declarations' },
  { key:'delete_declarations',  label:'Delete Declarations' },
  { key:'view_royalties',       label:'View Royalties' },
  { key:'edit_royalties',       label:'Edit Royalties' },
  { key:'delete_royalties',     label:'Delete Royalties' },
  { key:'view_profit',          label:'View Profit' },
  { key:'edit_profit',          label:'Edit Profit' },
  { key:'view_branches',        label:'View Branches' },
  { key:'edit_branches',        label:'Edit Branches' },
  { key:'view_users',           label:'View Users' },
  { key:'edit_users',           label:'Edit Users' },
  { key:'view_pipeline',        label:'View Pipeline' },
  { key:'edit_pipeline',        label:'Edit Pipeline' },
  { key:'view_compliance',      label:'View Compliance' },
  { key:'edit_compliance',      label:'Edit Compliance' },
  { key:'view_activity',        label:'View Activity' },
  { key:'view_cfo',             label:'View CFO Dashboard' },
  { key:'view_xero',            label:'View Xero' },
  { key:'manage_xero',          label:'Manage Xero' },
];
export default function Users({ user, directorView }) {
  const [users, setUsers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showToast, Toast] = useToast();
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const [permissionsModal, setPermissionsModal] = useState(null);
  const [franchiseAccessModal, setFranchiseAccessModal] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [permissions, setPermissions] = useState({});
  const [form, setForm] = useState({ name:'', email:'', role:'franchisee', password:'' });
  const [editForm, setEditForm] = useState({ name:'', email:'', role:'franchisee' });
  const isAdmin = user?.role === 'admin';
  const isAdminOrHQ = user?.role === 'admin' || user?.role === 'hq_support';
  const [selectedUser, setSelectedUser] = useState(null);
  const [gotutorsLoading, setGotutorsLoading] = useState({});
  const load = () => {
    Promise.all([api.getUsers(), api.getFranchises(directorView)]).then(([u, f]) => {
      setUsers(u || []);
      setFranchises(f || []);
      setLoading(false);
    });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedUser && users.length > 0) {
      const hash = window.location.hash.replace('#', '');
      const match = hash.match(/^users:([a-f0-9-]{36})$/);
      if (match) {
        const found = users.find(u => u.id === match[1]);
        if (found) setSelectedUser(found);
      }
    }
  }, [users]);
  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || (u.name||'').toLowerCase().includes(q) || (u.email||'').toLowerCase().includes(q);
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });
  const roles = ['all','admin','hq_support','director','franchisee','investor'];
  const roleCounts = {};
  roles.forEach(r => { roleCounts[r] = r==='all' ? users.length : users.filter(u=>u.role===r).length; });
  const roleColor = { admin:'red', hq_support:'amber', director:'blue', franchisee:'green', investor:'navy' };
  async function createUser() {
    if (!form.name || !form.email || !form.password) { showToast('Please fill all fields'); return; }
    await api.createUser(form);
    await api.logActivity('Created user', `${form.name} (${form.email}) — Role: ${form.role}`);
    showToast('User created');
    setCreateModal(false);
    setForm({ name:'', email:'', role:'franchisee', password:'' });
    load();
  }
  async function saveEdit() {
    if (!editForm.name || !editForm.email) { showToast('Please fill all fields'); return; }
    const orig = users.find(u => u.id === editModal.id);
    const changes = [];
    if (orig.name !== editForm.name) changes.push(`Name: "${orig.name}" → "${editForm.name}"`);
    if (orig.email !== editForm.email) changes.push(`Email: "${orig.email}" → "${editForm.email}"`);
    if (orig.role !== editForm.role) changes.push(`Role: "${orig.role}" → "${editForm.role}"`);
    await api.editUser(editModal.id, editForm);
    await api.logActivity('Updated user details', changes.length ? changes.join(', ') : 'No changes');
    showToast('User updated'); setEditModal(null); load();
  }
  async function resetPassword() {
    if (!newPw || newPw.length < 8) { showToast('Password must be at least 8 characters'); return; }
    await api.resetPassword(resetModal.id, newPw);
    await api.logActivity('Reset password', `${resetModal.name} (${resetModal.email})`);
    showToast(`Password reset for ${resetModal.name}`);
    setResetModal(null); setNewPw('');
  }
  async function toggleActive(u) {
    await api.updateUser(u.id, { active:!u.active });
    await api.logActivity(u.active?'Suspended user':'Reactivated user', `${u.name} (${u.email})`);
    showToast(u.active ? `${u.name} suspended` : `${u.name} reactivated`);
    setUsers(users.map(x => x.id===u.id ? {...x, active:!u.active} : x));
  }
  async function toggleCFO(u) {
    await api.updateUser(u.id, { cfo_access:!u.cfo_access });
    showToast((u.cfo_access?'CFO access removed from ':'CFO access granted to ')+u.name);
    setUsers(users.map(x => x.id===u.id ? {...x, cfo_access:!u.cfo_access} : x));
  }
  async function toggleGotutors(u) {
    const enable = !u.gotutorsAccess;
    setGotutorsLoading(prev => ({ ...prev, [u.id]: true }));
    try {
      const res = await api.toggleGotutorsAccess(u.id, enable);
      if (res?.error) {
        showToast(`Error: ${res.error}`, 'error');
      } else {
        showToast(enable ? `${u.name} added to GoTutors portal` : `${u.name} disabled on GoTutors portal`);
        setUsers(users.map(x => x.id===u.id ? {...x, gotutorsAccess:enable, gotutorsUserId: res?.gotutorsUserId || x.gotutorsUserId} : x));
        if (selectedUser?.id === u.id) setSelectedUser(s => ({...s, gotutorsAccess:enable}));
      }
    } catch(e) {
      showToast('Failed to update GoTutors access', 'error');
    }
    setGotutorsLoading(prev => ({ ...prev, [u.id]: false }));
  }
  async function deleteUser(u) {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    await api.deleteUser(u.id);
    await api.logActivity('Deleted user', `${u.name} (${u.email}) — Role: ${u.role}`);
    showToast('User deleted');
    setUsers(users.filter(x => x.id!==u.id));
  }
  async function savePermissions() {
    await api.updatePermissions(permissionsModal.id, permissions);
    await api.logActivity('Updated permissions', `${permissionsModal.name} — ${Object.keys(permissions).filter(k=>permissions[k]).length} permissions enabled`);
    showToast('Permissions saved'); setPermissionsModal(null); load();
  }
  function downloadCSV() {
    const headers = ['Name','Email','Role','Active','CFO Access','GoTutors Access','Created'];
    const rows = filtered.map(u => [
      u.name, u.email, u.role,
      u.active?'Yes':'No',
      u.cfo_access?'Yes':'No',
      u.gotutorsAccess?'Yes':'No',
      u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-GB') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${(v||'').toString().replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = 'users.csv'; a.click();
  }
  function getUserFranchises(u) {
    const result = [];
    franchises.forEach(f => {
      const owner = (f.owners||[]).find(o => (o.email||'').toLowerCase() === (u.email||'').toLowerCase());
      const investor = (f.investors||[]).find(i => (i.email||'').toLowerCase() === (u.email||'').toLowerCase());
      if (owner) result.push({ franchise:f, role:'Owner', sharePercent: owner.sharePercent||owner.share_percent||null });
      else if (investor) result.push({ franchise:f, role:'Investor', sharePercent: investor.sharePercent||investor.share_percent||null });
    });
    return result;
  }
  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Users" /><Spinner /></div>;

  // Toggle component reused in table and profile
  function GotutorsToggle({ u }) {
    const isLoading = gotutorsLoading[u.id];
    return (
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <div
          onClick={() => !isLoading && isAdmin && toggleGotutors(u)}
          style={{
            width:40, height:22, borderRadius:11,
            background: u.gotutorsAccess ? '#4F46E5' : '#e5e7eb',
            cursor: isAdmin ? 'pointer' : 'default',
            position:'relative', transition:'background 0.2s',
            opacity: isLoading ? 0.6 : 1,
          }}>
          <div style={{
            width:16, height:16, borderRadius:'50%', background:'#fff',
            position:'absolute', top:3, left: u.gotutorsAccess ? 21 : 3,
            transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </div>
        {u.gotutorsAccess && <span style={{ fontSize:10, color:'#4F46E5', fontWeight:600 }}>Active</span>}
        {isLoading && <span style={{ fontSize:10, color:'var(--text-tertiary)' }}>...</span>}
      </div>
    );
  }

  // User profile view
  if (selectedUser) {
    const u = selectedUser;
    const userFranchises = getUserFranchises(u);
    return (
      <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
        <Topbar title={u.name} subtitle={u.email} actions={
          <div style={{ display:'flex', gap:8 }}>
            {isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => { setEditModal(u); setEditForm({name:u.name,email:u.email,role:u.role||'franchisee'}); }}>Edit</button>}
            {isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => { setResetModal(u); setNewPw(''); }}>Reset PW</button>}
            {isAdmin && <button className="btn btn-sm" style={{ background:u.active?'var(--amber-bg)':'var(--green-bg)', color:u.active?'var(--amber)':'var(--green)', border:'none' }} onClick={() => toggleActive(u)}>{u.active?'Suspend':'Activate'}</button>}
            <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedUser(null); window.location.hash = 'users'; }}>← Back</button>
          </div>
        } />
        {Toast}
        <div style={{ padding:24 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
            <div className="card" style={{ padding:20 }}>
              <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20 }}>
                <div style={{ width:56, height:56, borderRadius:'50%', background:u.active?'var(--navy)':'#e5e7eb', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:700, color:u.active?'#fff':'#9ca3af', flexShrink:0 }}>
                  {(u.name||'?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:17, color:'var(--navy)' }}>{u.name}</div>
                  <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:2 }}>{u.email}</div>
                  <div style={{ display:'flex', gap:6, marginTop:6, flexWrap:'wrap' }}>
                    <Badge type={roleColor[u.role]||'navy'}>{u.role==='hq_support'?'HQ Support':u.role}</Badge>
                    <Badge type={u.active?'green':'red'}>{u.active?'Active':'Suspended'}</Badge>
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {u.phone && <div><label>PHONE</label><div style={{ fontSize:13 }}>{u.phone}</div></div>}
                <div><label>CREATED</label><div style={{ fontSize:13 }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : '—'}</div></div>
                <div><label>LAST LOGIN</label><div style={{ fontSize:13 }}>{u.lastLogin ? new Date(u.lastLogin).toLocaleString('en-GB') : 'Never'}</div></div>
                {u.cfo_access && <div><Badge type="amber">CFO Access enabled</Badge></div>}
                {/* GoTutors Access in profile */}
                {isAdmin && (
                  <div style={{ marginTop:4 }}>
                    <label>GOTUTORS PORTAL ACCESS</label>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:6, flexWrap:'wrap' }}>
                      <GotutorsToggle u={u} />
                      {u.gotutorsAccess
                        ? <span style={{ fontSize:12, color:'#4F46E5' }}>User has access to support.gotutors.com</span>
                        : <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>No access to support portal</span>
                      }
                      {u.gotutorsAccess && (
                        <button
                          className="btn btn-sm btn-secondary"
                          style={{ fontSize:11, marginLeft:4 }}
                          onClick={async () => {
                            try {
                              await api.sendWelcomeEmail(u.id);
                              showToast(`Welcome email sent to ${u.email}`);
                            } catch(e) {
                              showToast('Failed to send welcome email');
                            }
                          }}>
                          ✉ Send Welcome Email
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:14 }}>FRANCHISE ACCESS</div>
              {userFranchises.length === 0 ? (
                <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Not linked to any franchises</div>
              ) : userFranchises.map((f,i) => (
                <div key={i} style={{ padding:'10px 12px', background:'var(--bg)', borderRadius:'var(--r-md)', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontWeight:500, fontSize:13 }}>{f.franchise.name}</div>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{f.franchise.location}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <Badge type={f.role==='Owner'?'navy':'blue'}>{f.role}</Badge>
                    {f.sharePercent && <span style={{ fontSize:11, color:'var(--text-tertiary)', fontWeight:600 }}>{parseFloat(f.sharePercent).toFixed(2)}%</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {u.role === 'hq_support' && isAdmin && (
            <div className="card" style={{ padding:20, marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em' }}>PERMISSIONS</div>
                <button className="btn btn-sm btn-secondary" onClick={() => { setPermissionsModal(u); setPermissions(u.permissions||{}); }}>Manage Permissions</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
                {PERMISSIONS.map(p => (
                  <div key={p.key} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, padding:'6px 8px', borderRadius:'var(--r-sm)', background:(u.permissions||{})[p.key]?'var(--green-bg)':'#f9fafb' }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:(u.permissions||{})[p.key]?'var(--green)':'#e2e8f0', flexShrink:0 }} />
                    {p.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {editModal && (
          <Modal title="Edit User" onClose={() => setEditModal(null)}
            footer={<><button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button><button className="btn btn-primary" onClick={async () => {
              await api.editUser(editModal.id, editForm);
              showToast('Updated'); setEditModal(null); load();
            }}>Save</button></>}>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div><label>FULL NAME</label><input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} /></div>
              <div><label>EMAIL</label><input type="email" value={editForm.email} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))} /></div>
            <div><label>ROLE</label><select value={editForm.role} onChange={e=>setEditForm(f=>({...f,role:e.target.value}))}>
              <option value="admin">Admin</option>
              <option value="hq_support">HQ Support</option>
              <option value="director">Director</option>
              <option value="franchisee">Franchisee</option>
              <option value="investor">Investor</option>
            </select></div>
            </div>
          </Modal>
        )}
        {resetModal && (
          <Modal title={`Reset Password — ${resetModal.name}`} onClose={() => setResetModal(null)}
            footer={<><button className="btn btn-secondary" onClick={() => setResetModal(null)}>Cancel</button><button className="btn btn-primary" onClick={async () => {
              if (!newPw || newPw.length < 8) { showToast('Min 8 characters'); return; }
              await api.resetPassword(resetModal.id, newPw);
              showToast('Password reset'); setResetModal(null);
            }}>Reset</button></>}>
            <div><label>NEW PASSWORD</label><input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Min 8 characters" /></div>
          </Modal>
        )}
        {permissionsModal && (
          <Modal title={`Permissions — ${permissionsModal.name}`} onClose={() => setPermissionsModal(null)} width={540}
            footer={<>
              <button className="btn btn-secondary" onClick={() => setPermissions(Object.fromEntries(PERMISSIONS.map(p=>[p.key,false])))}>Clear All</button>
              <button className="btn btn-secondary" onClick={() => setPermissions(Object.fromEntries(PERMISSIONS.map(p=>[p.key,true])))}>Grant All</button>
              <button className="btn btn-primary" onClick={async () => { await api.updatePermissions(permissionsModal.id, permissions); showToast('Saved'); setPermissionsModal(null); load(); }}>Save</button>
            </>}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {PERMISSIONS.map(p => (
                <label key={p.key} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, padding:'8px 10px', borderRadius:'var(--r-md)', background:permissions[p.key]?'var(--blue-bg)':'#f9fafb', border:'1px solid var(--border)' }}>
                  <input type="checkbox" checked={!!permissions[p.key]} onChange={e=>setPermissions(pp=>({...pp,[p.key]:e.target.checked}))} />
                  {p.label}
                </label>
              ))}
            </div>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Users" subtitle={`${users.length} total`} actions={
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={downloadCSV}>
            <i className="ti ti-download" style={{ fontSize:13 }} /> Export CSV
          </button>
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => setCreateModal(true)}>
              <i className="ti ti-plus" style={{ fontSize:14 }} /> New User
            </button>
          )}
        </div>
      } />
      {Toast}
      <div style={{ padding:24 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, marginBottom:16 }}>
          {[
            { role:'admin', label:'Admins', color:'var(--red)' },
            { role:'hq_support', label:'HQ Support', color:'var(--amber)' },
            { role:'director', label:'Directors', color:'var(--blue)' },
            { role:'franchisee', label:'Franchisees', color:'var(--green)' },
            { role:'investor', label:'Investors', color:'var(--navy)' },
          ].map(s => (
            <div key={s.role} className="card" style={{ padding:'10px 14px', cursor:'pointer', border: roleFilter===s.role?`2px solid ${s.color}`:'1px solid var(--border)' }} onClick={()=>setRoleFilter(roleFilter===s.role?'all':s.role)}>
              <div style={{ fontSize:10, color:'var(--text-tertiary)' }}>{s.label}</div>
              <div style={{ fontSize:20, fontWeight:700, color:s.color }}>{roleCounts[s.role]}</div>
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
          <div style={{ flex:1, maxWidth:320 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name or email..." />
          </div>
          <button onClick={()=>setRoleFilter('all')} className="btn btn-sm btn-secondary">Show All ({users.length})</button>
        </div>
        <div className="card" style={{ overflow:'hidden' }}>
          {filtered.length === 0 ? <EmptyState icon="ti-users" message="No users found" /> : (
            <div style={{ overflowX:'auto' }}>
              <table className="table">
                <thead><tr>
                  <th>USER</th><th>ROLE</th><th>FRANCHISES</th><th>STATUS</th><th>CFO</th><th>GOTUTORS</th>
                  {isAdminOrHQ && <th>ACTIONS</th>}
                </tr></thead>
                <tbody>
                  {filtered.map(u => {
                    const userFranchises = getUserFranchises(u);
                    const initials = (u.name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
                    return (
                      <tr key={u.id} style={{ cursor:'pointer' }} onClick={() => { setSelectedUser(u); window.location.hash = 'users:' + u.id; }}>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <div style={{ width:34, height:34, borderRadius:'50%', background:u.active?'var(--navy)':'#e5e7eb', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:600, color:u.active?'#fff':'#9ca3af', flexShrink:0 }}>
                              {initials}
                            </div>
                            <div>
                              <div style={{ fontWeight:500, fontSize:13 }}>{u.name}</div>
                              <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><Badge type={roleColor[u.role]||'navy'}>{u.role==='hq_support'?'HQ Support':u.role}</Badge></td>
                        <td>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {userFranchises.slice(0,2).map((f,i) => (
                              <span key={i} style={{ fontSize:11, padding:'2px 8px', background:'var(--blue-bg)', borderRadius:20, color:'var(--navy)', border:'1px solid var(--border)' }}>
                                {f.franchise.location||f.franchise.name}
                              </span>
                            ))}
                            {userFranchises.length > 2 && <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>+{userFranchises.length-2}</span>}
                            {userFranchises.length === 0 && <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>—</span>}
                          </div>
                        </td>
                        <td><Badge type={u.active?'green':'red'}>{u.active?'Active':'Suspended'}</Badge></td>
                        <td>
                          {(u.role==='hq_support'||u.role==='admin') && isAdmin ? (
                            <div onClick={e=>{e.stopPropagation();toggleCFO(u);}} style={{ width:40, height:22, borderRadius:11, background:u.cfo_access?'var(--green)':'#e5e7eb', cursor:'pointer', position:'relative', transition:'background 0.2s' }}>
                              <div style={{ width:16, height:16, borderRadius:'50%', background:'#fff', position:'absolute', top:3, left:u.cfo_access?21:3, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                            </div>
                          ) : <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>—</span>}
                        </td>
                        <td onClick={e=>e.stopPropagation()}>
                          {isAdmin && (u.role==='franchisee'||u.role==='director'||u.role==='hq_support') ? (
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <div
                                onClick={() => !gotutorsLoading[u.id] && toggleGotutors(u)}
                                style={{ width:40, height:22, borderRadius:11, background:u.gotutorsAccess?'#4F46E5':'#e5e7eb', cursor:'pointer', position:'relative', transition:'background 0.2s', opacity:gotutorsLoading[u.id]?0.6:1 }}>
                                <div style={{ width:16, height:16, borderRadius:'50%', background:'#fff', position:'absolute', top:3, left:u.gotutorsAccess?21:3, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                              </div>
                              {gotutorsLoading[u.id] && <span style={{ fontSize:10, color:'var(--text-tertiary)' }}>...</span>}
                            </div>
                          ) : <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>—</span>}
                        </td>
                        {isAdminOrHQ && (
                          <td onClick={e=>e.stopPropagation()}>
                            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                              {isAdmin && <button className="btn btn-sm btn-secondary" style={{ fontSize:11 }} onClick={() => { setEditModal(u); setEditForm({name:u.name,email:u.email,role:u.role||'franchisee'}); }}>Edit</button>}
                              {isAdmin && <button className="btn btn-sm btn-secondary" style={{ fontSize:11 }} onClick={() => { setResetModal(u); setNewPw(''); }}>Reset PW</button>}
                              {u.role==='hq_support' && isAdmin && (
                                <button className="btn btn-sm btn-secondary" style={{ fontSize:11 }} onClick={() => { setPermissionsModal(u); setPermissions(u.permissions || {}); }}>Permissions</button>
                              )}
                              {isAdmin && (
                                <button className="btn btn-sm" style={{ background:u.active?'var(--amber-bg)':'var(--green-bg)', color:u.active?'var(--amber)':'var(--green)', border:'none', fontSize:11 }} onClick={()=>toggleActive(u)}>
                                  {u.active?'Suspend':'Activate'}
                                </button>
                              )}
                              {isAdmin && <button className="btn btn-sm btn-danger" style={{ fontSize:11 }} onClick={()=>deleteUser(u)}>Delete</button>}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {createModal && (
        <Modal title="Create New User" onClose={()=>setCreateModal(false)}
          footer={<><button className="btn btn-secondary" onClick={()=>setCreateModal(false)}>Cancel</button><button className="btn btn-primary" onClick={createUser}>Create</button></>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>FULL NAME *</label><input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Full name" /></div>
            <div><label>EMAIL *</label><input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="email@example.com" /></div>
            <div><label>ROLE</label>
              <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
                <option value="franchisee">Franchisee</option>
                <option value="investor">Investor</option>
                <option value="hq_support">HQ Support</option>
                <option value="director">Director</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div><label>PASSWORD *</label><input type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Min 8 characters" /></div>
          </div>
        </Modal>
      )}
      {editModal && (
        <Modal title="Edit User" onClose={()=>setEditModal(null)}
          footer={<><button className="btn btn-secondary" onClick={()=>setEditModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveEdit}>Save</button></>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>FULL NAME</label><input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} /></div>
            <div><label>EMAIL</label><input type="email" value={editForm.email} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))} /></div>
          </div>
        </Modal>
      )}
      {resetModal && (
        <Modal title={`Reset Password — ${resetModal.name}`} onClose={()=>setResetModal(null)}
          footer={<><button className="btn btn-secondary" onClick={()=>setResetModal(null)}>Cancel</button><button className="btn btn-primary" onClick={resetPassword}>Reset</button></>}>
          <div><label>NEW PASSWORD (min 8 characters)</label><input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="New password" /></div>
        </Modal>
      )}
      {permissionsModal && (
        <Modal title={`Permissions — ${permissionsModal.name}`} onClose={()=>setPermissionsModal(null)} width={540}
          footer={<>
            <button className="btn btn-secondary" onClick={()=>setPermissionsModal(null)}>Cancel</button>
            <button className="btn btn-secondary" onClick={()=>setPermissions(Object.fromEntries(PERMISSIONS.map(p=>[p.key,false])))}>Clear All</button>
            <button className="btn btn-primary" onClick={()=>setPermissions(Object.fromEntries(PERMISSIONS.map(p=>[p.key,true])))}>Grant All</button>
            <button className="btn btn-primary" onClick={savePermissions}>Save</button>
          </>}>
          <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:14 }}>Configure what this HQ Support user can access.</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {PERMISSIONS.map(p => (
              <label key={p.key} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, padding:'8px 10px', borderRadius:'var(--r-md)', background:permissions[p.key]?'var(--blue-bg)':'#f9fafb', border:'1px solid var(--border)' }}>
                <input type="checkbox" checked={!!permissions[p.key]} onChange={e=>setPermissions(pp=>({...pp,[p.key]:e.target.checked}))} />
                {p.label}
              </label>
            ))}
          </div>
        </Modal>
      )}
      {franchiseAccessModal && (
        <Modal title={`Franchise Access — ${franchiseAccessModal.name}`} onClose={()=>setFranchiseAccessModal(null)} width={520}
          footer={<button className="btn btn-secondary" onClick={()=>setFranchiseAccessModal(null)}>Close</button>}>
          {(() => {
            const uFranchises = getUserFranchises(franchiseAccessModal);
            return uFranchises.length === 0 ? (
              <div style={{ textAlign:'center', padding:'24px 0', color:'var(--text-tertiary)', fontSize:13 }}>Not linked to any franchises</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {uFranchises.map((f,i) => (
                  <div key={i} style={{ padding:'12px 14px', border:'1px solid var(--border)', borderRadius:'var(--r-md)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:500, fontSize:13 }}>{f.franchise.name}</div>
                      <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{f.franchise.location}</div>
                    </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <Badge type={f.role==='Owner'?'navy':'blue'}>{f.role}</Badge>
                    {f.sharePercent && <span style={{ fontSize:12, color:'var(--text-tertiary)', fontWeight:600 }}>{parseFloat(f.sharePercent).toFixed(2)}%</span>}
                  </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}
